'use es6';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["currency", "formatter", "formatNegativeValuesWithParenthesis", "isNativeCurrency", "locale", "parser", "precision"];
import I18n from 'I18n';
import { formatIntlCurrency } from 'I18n/utils/formatIntlCurrency';
import memoizeOne from 'react-utils/memoizeOne';
import PropTypes from 'prop-types';
import { PureComponent } from 'react';
import omit from '../utils/underscore/omit';
import Controllable from '../decorators/Controllable';
import ShareInput from '../decorators/ShareInput';
import UIAbstractNumberInput from './abstract/UIAbstractNumberInput';
import UITextInput from './UITextInput';
import { jsx as _jsx } from "react/jsx-runtime";
const BLANK_REGEX = /^\s*$/;
const formatUnsupportedCurrency = (currency, value, formatNegativeValuesWithParenthesis) => {
  if (formatNegativeValuesWithParenthesis) {
    if (value < 0) {
      return `(${currency} ${-value})`;
    }
  }
  return `${currency} ${value}`;
};
const getCurrencySymbol = (currency, isNativeCurrency) => {
  const symbolData = I18n.currencySymbols[currency];
  if (!symbolData) return currency;
  return (isNativeCurrency ? symbolData.symbol_native : symbolData.symbol) || currency;
};
const getCurrencyPrecision = (currency, value) => {
  const symbolData = I18n.currencySymbols[currency];
  if (!symbolData) {
    return 2;
  }
  return symbolData.alternative_decimal_digits && value % 1 !== 0 ? symbolData.alternative_decimal_digits : symbolData.decimal_digits;
};
const getCurrencyFormatter = (currency, isNativeCurrency, formatNegativeValuesWithParenthesis, locale) => (value, {
  maxFractionDigits,
  precision
} = {}) => {
  if (value == null) return '';
  const computedPrecision = precision || getCurrencyPrecision(currency, value);
  let formattedCurrency;
  try {
    formattedCurrency = formatIntlCurrency(value, {
      locale,
      currencyCode: currency,
      currencySign: formatNegativeValuesWithParenthesis ? 'accounting' : 'standard',
      precision: maxFractionDigits ? Math.max(computedPrecision, maxFractionDigits) : computedPrecision,
      parts: {
        currency: getCurrencySymbol(currency, isNativeCurrency),
        // Backwards compatible formatting for CLP: https://git.hubteam.com/HubSpot/UIComponents/pull/10155#discussion_r1772511
        literal: currency === 'CLP' ? '' : undefined
      }
    });
  } catch (e) {
    formattedCurrency = formatUnsupportedCurrency(currency, value, formatNegativeValuesWithParenthesis);
  }
  return formattedCurrency;
};
const getCurrencyParser = (currency, isNativeCurrency, locale) => rawString => {
  const currencySymbol = getCurrencySymbol(currency, isNativeCurrency);
  const currencySymbolRegex = new RegExp(`${currencySymbol.replace(/\W/g, '\\$&')}`, 'g');
  const valueToParse = rawString.replace(currencySymbolRegex, '');
  return BLANK_REGEX.test(valueToParse) ? null : I18n.parseNumber(valueToParse, {
    locale
  });
};
class UICurrencyInput extends PureComponent {
  constructor(props) {
    super(props);
    this._getCurrencyFormatter = memoizeOne(getCurrencyFormatter);
    this._getCurrencyParser = memoizeOne(getCurrencyParser);
  }
  render() {
    const _this$props = this.props,
      {
        currency,
        formatter,
        formatNegativeValuesWithParenthesis,
        isNativeCurrency,
        locale,
        parser,
        precision
      } = _this$props,
      rest = _objectWithoutPropertiesLoose(_this$props, _excluded);
    return /*#__PURE__*/_jsx(UIAbstractNumberInput, Object.assign({}, rest, {
      formatter: formatter || currency && this._getCurrencyFormatter(currency, isNativeCurrency, formatNegativeValuesWithParenthesis, locale),
      parser: parser || currency && this._getCurrencyParser(currency, isNativeCurrency, locale),
      precision: precision,
      children: numberProps => /*#__PURE__*/_jsx(UITextInput, Object.assign({}, numberProps, {
        type: "text" /* Other `type` prop values are ignored; see #4178 */
      }))
    }));
  }
}
UICurrencyInput.displayName = 'UICurrencyInput';
UICurrencyInput.defaultProps = Object.assign({}, UITextInput.defaultProps, UIAbstractNumberInput.defaultProps, {
  isNativeCurrency: true,
  onRawValueChange: () => {},
  formatNegativeValuesWithParenthesis: false
});
UICurrencyInput.propTypes = Object.assign({}, omit(UITextInput.propTypes, ['type']), omit(UIAbstractNumberInput.propTypes, ['children', 'currency', 'formatStyle']), {
  currency: PropTypes.string.isRequired,
  isNativeCurrency: PropTypes.bool.isRequired,
  onRawValueChange: PropTypes.func,
  /**
   * Used to format currency values with high precision. When set, a currency
   * input will display up to `maxFractionDigits` of precision, favoring the
   * currency's default precision if more precise.
   */
  maxFractionDigits: PropTypes.number,
  formatNegativeValuesWithParenthesis: PropTypes.bool,
  locale: PropTypes.string
});
export default ShareInput(Controllable(UICurrencyInput));